import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, Container, Grid, Card, CardContent, CardMedia, ThemeProvider, } from "@mui/material";
import { HLSVideoPlayer } from "./components/HLSVideoPlayer";
import { Helmet } from "react-helmet-async";
import Footer from "./Footer";
import getTheme from "./Theme";
import { getCurrentLocalhostPort } from "./util/PortUtils";
export default function Welcome() {
    const navigate = useNavigate();
    const theme = getTheme();
    const getSignupURL = () => {
        // Beta & Prod share a user pool
        const BASE_URL = "https://auth.mediashare.link/signup?response_type=code&client_id=5g9240lj7oj1ckpr7c9fsasss1&scope=openid+aws.cognito.signin.user.admin+profile+email+phone&redirect_uri=";
        const localhostPort = getCurrentLocalhostPort();
        if (localhostPort) {
            return BASE_URL + `http://localhost:${localhostPort}/authcb.html`;
        }
        else if (window.location.href.includes("test.mediashare.link")) {
            return BASE_URL + "https://test.mediashare.link/authcb.html";
        }
        else {
            return BASE_URL + "https://www.mediashare.link/authcb.html";
        }
    };
    const handleCreateAccount = () => {
        // Redirect to Cognito signup
        const signupUrl = getSignupURL();
        window.location.href = signupUrl;
    };
    const handleSignIn = () => {
        // Redirect to Login component which handles Cognito
        navigate("/Login");
    };
    const features = [
        {
            title: "Share Latest Moments",
            description: "Share your latest photos and videos with friends and family instantly",
            image: "img/latest_512.jpg",
        },
        {
            title: "Social Feed",
            description: "Stay connected with personalized feeds from your network",
            image: "img/feed_512.jpg",
        },
        {
            title: "Connect with Friends",
            description: "Build meaningful connections and stay in touch with loved ones",
            image: "img/friends_512.jpg",
        },
        {
            title: "AI Design Studio",
            description: "Create amazing AI-generated images and videos with our powerful creative tools",
            image: "img/painter-cat-512.jpg",
        },
        {
            title: "Private Messaging",
            description: "Send secure messages and have private conversations with your connections",
            image: "img/messaging_512.jpg",
        },
        {
            title: "Create Channels",
            description: "Organize and curate content by creating themed channels for your interests",
            image: "img/channels_512.jpg",
        },
    ];
    const backgroundVideo = (_jsx(HLSVideoPlayer, { src: `${window.location.origin}/img/background/background.convert.m3u8`, autoplay: true, muted: true, loop: true, controls: false, style: {
            width: "100%",
            height: "300px",
            objectFit: "cover",
        }, cannotPlayImageUrl: "img/public_splash.jpg" }));
    return (_jsxs(ThemeProvider, Object.assign({ theme: theme }, { children: [_jsxs(Helmet, { children: [_jsx("title", { children: "Welcome to MediaShare - Free Social Network for Photo & Video Sharing" }), _jsx("meta", { name: "description", content: "Join MediaShare's free social network to share photos and videos with friends and family. Create your account today and start connecting!" })] }), _jsxs(Box, Object.assign({ sx: {
                    minHeight: "100vh",
                    backgroundColor: "#f8f6ff",
                    display: "flex",
                    flexDirection: "column",
                } }, { children: [_jsxs(Container, Object.assign({ maxWidth: "lg", sx: { flex: 1, py: 1 } }, { children: [_jsxs(Box, Object.assign({ sx: {
                                    textAlign: "center",
                                    mb: 2,
                                    py: 1,
                                } }, { children: [_jsx(Typography, Object.assign({ variant: "body2", sx: {
                                            color: theme.palette.text.secondary,
                                            mb: 2,
                                        } }, { children: "Already have an account?" })), _jsxs(Box, Object.assign({ sx: {
                                            display: "flex",
                                            gap: 2,
                                            justifyContent: "center",
                                            flexDirection: { xs: "column", sm: "row" },
                                            alignItems: "center",
                                        } }, { children: [_jsx(Button, Object.assign({ variant: "outlined", size: "small", onClick: handleSignIn, sx: {
                                                    px: 3,
                                                    py: 1,
                                                    fontSize: "0.9rem",
                                                    fontWeight: "bold",
                                                    borderRadius: "6px",
                                                }, "aria-label": "Sign In Button" }, { children: "Sign In" })), _jsx(Button, Object.assign({ variant: "text", size: "small", onClick: handleCreateAccount, sx: {
                                                    px: 3,
                                                    py: 1,
                                                    fontSize: "0.9rem",
                                                    fontWeight: "bold",
                                                } }, { children: "Create Account" }))] }))] })), _jsx(Card, Object.assign({ elevation: 10, sx: {
                                    backgroundColor: "#FFFFFF",
                                    padding: 4,
                                    margin: "1rem auto 4rem auto",
                                    maxWidth: "900px",
                                    textAlign: "center",
                                } }, { children: _jsxs(CardContent, { children: [_jsx(Box, Object.assign({ sx: { mb: 3, textAlign: "center" } }, { children: _jsx("img", { src: "img/logo.svg", alt: "MediaShare.Link Logo", style: {
                                                    width: "280px",
                                                    maxWidth: "100%",
                                                    height: "auto",
                                                } }) })), _jsx(Typography, Object.assign({ variant: "h3", component: "h1", color: "primary", sx: {
                                                fontWeight: "bold",
                                                mb: 1,
                                                fontSize: { xs: "1.8rem", sm: "2.5rem", md: "3rem" },
                                                lineHeight: 1.2,
                                                px: { xs: 1, sm: 0 },
                                            } }, { children: "Welcome to MediaShare.Link" })), _jsx(Typography, Object.assign({ variant: "h6", color: "primary", sx: {
                                                mb: 4,
                                                fontWeight: 500,
                                                fontSize: { xs: "1rem", sm: "1.25rem" },
                                                px: { xs: 1, sm: 0 },
                                            } }, { children: "A Social Media Network" })), _jsx(Box, Object.assign({ sx: {
                                                mb: 4,
                                            } }, { children: backgroundVideo })), _jsx(Typography, Object.assign({ variant: "h4", color: "primary", sx: {
                                                mb: 3,
                                                fontWeight: "600",
                                            } }, { children: "Everyone is Welcome Here! \uD83C\uDF89" })), _jsx(Typography, Object.assign({ variant: "body1", sx: {
                                                color: theme.palette.text.secondary,
                                                fontSize: "1.1rem",
                                                lineHeight: 1.6,
                                                mb: 4,
                                                maxWidth: "700px",
                                                mx: "auto",
                                            } }, { children: "We are thrilled that you have chosen to visit MediaShare! Our free social network makes it easy and fun to share your favorite photos and videos with friends and family. Join our growing community where meaningful connections are made through shared moments and memories." })), _jsx(Typography, Object.assign({ variant: "h5", color: "primary", sx: {
                                                mb: 3,
                                                fontWeight: "600",
                                            } }, { children: "Ready to Get Started?" })), _jsxs(Box, Object.assign({ sx: {
                                                display: "flex",
                                                gap: 3,
                                                justifyContent: "center",
                                                flexDirection: { xs: "column", sm: "row" },
                                                alignItems: "center",
                                            } }, { children: [_jsx(Button, Object.assign({ variant: "contained", size: "large", onClick: handleCreateAccount, sx: {
                                                        px: 4,
                                                        py: 1.5,
                                                        fontSize: "1.1rem",
                                                        fontWeight: "bold",
                                                        borderRadius: "8px",
                                                    } }, { children: "Create New Account" })), _jsx(Button, Object.assign({ variant: "outlined", size: "large", onClick: handleSignIn, sx: {
                                                        px: 4,
                                                        py: 1.5,
                                                        fontSize: "1.1rem",
                                                        fontWeight: "bold",
                                                        borderRadius: "8px",
                                                    } }, { children: "Sign In to Existing Account" }))] }))] }) })), _jsx(Grid, Object.assign({ container: true, spacing: 3, sx: { mb: 4 } }, { children: features.map((feature, index) => (_jsx(Grid, Object.assign({ item: true, xs: 12, md: 4 }, { children: _jsxs(Card, Object.assign({ sx: {
                                            height: "100%",
                                            boxShadow: 2,
                                            transition: "transform 0.2s ease, box-shadow 0.2s ease",
                                            "&:hover": {
                                                transform: "translateY(-4px)",
                                                boxShadow: 4,
                                            },
                                        } }, { children: [_jsx(CardMedia, { component: "img", height: "160", image: feature.image, alt: feature.title, sx: { objectFit: "cover" } }), _jsxs(CardContent, Object.assign({ sx: { p: 3 } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", component: "h3", color: "primary", sx: {
                                                            mb: 2,
                                                            fontWeight: "bold",
                                                        } }, { children: feature.title })), _jsx(Typography, Object.assign({ variant: "body2", sx: {
                                                            color: theme.palette.text.secondary,
                                                            lineHeight: 1.6,
                                                        } }, { children: feature.description }))] }))] })) }), index))) })), _jsx(Card, Object.assign({ elevation: 5, sx: {
                                    backgroundColor: "#FFFFFF",
                                    borderRadius: "12px",
                                    p: 4,
                                    textAlign: "center",
                                    mb: 4,
                                } }, { children: _jsxs(CardContent, { children: [_jsx(Typography, Object.assign({ variant: "body1", sx: {
                                                color: theme.palette.text.secondary,
                                                fontSize: "1rem",
                                                fontStyle: "italic",
                                                mb: 3,
                                            } }, { children: "It is completely free to join, create an account, and start sharing. We hope you have an amazing time connecting with others!" })), _jsxs(Box, Object.assign({ sx: {
                                                display: "flex",
                                                gap: 3,
                                                justifyContent: "center",
                                                flexDirection: { xs: "column", sm: "row" },
                                                alignItems: "center",
                                            } }, { children: [_jsx(Button, Object.assign({ variant: "contained", size: "large", onClick: handleCreateAccount, sx: {
                                                        px: 4,
                                                        py: 1.5,
                                                        fontSize: "1.1rem",
                                                        fontWeight: "bold",
                                                        borderRadius: "8px",
                                                    } }, { children: "Create New Account" })), _jsx(Button, Object.assign({ variant: "outlined", size: "large", onClick: handleSignIn, sx: {
                                                        px: 4,
                                                        py: 1.5,
                                                        fontSize: "1.1rem",
                                                        fontWeight: "bold",
                                                        borderRadius: "8px",
                                                    } }, { children: "Sign In to Existing Account" }))] }))] }) }))] })), _jsx(Footer, {})] }))] })));
}
